import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx-js-style";
import React from "react";
import { getCompanyInfo } from "../shared/CommonFunctions";

// ---------------- Helper Functions ----------------

// Extract plain text from React elements
export const extractTextFromJSX = (element) => {
  if (typeof element === "string" || typeof element === "number") return element;
  if (React.isValidElement(element)) {
    if (typeof element.props.children === "string" || typeof element.props.children === "number")
      return element.props.children;
    if (Array.isArray(element.props.children))
      return element.props.children.map(extractTextFromJSX).join("");
  }
  return "";
};

// Generate file name with timestamp
export const getFileName = (baseName) => {
  const now = new Date();
  const timestamp = now.toISOString().replace(/[-:.]/g, ""); // YYYYMMDDTHHMMSS
  const safeBase =
    typeof baseName === "string" && baseName.trim() !== "" ? baseName.trim() : "Report";
  return `${safeBase}_${timestamp}`;
};

// ---------------- Excel Export ----------------
export const exportToExcel = async (title, columns, data, headerInfo = {}) => {
    if (!data || !columns) return;
  
    const wb = XLSX.utils.book_new();
    const ws = {};
  
    let rowCursor = 0;
    const now = new Date();
    const generatedOn = now.toLocaleString();
  
    // --- Fetch company info ---
    const companyInfo = await getCompanyInfo();
    const companyName = companyInfo?.CompanyName || "Company";
  
    // --- COMPANY NAME (Top Row, Centered) ---
    ws[XLSX.utils.encode_cell({ r: rowCursor, c: 0 })] = {
      v: companyName,
      t: "s",
      s: {
        font: { bold: true, sz: 16, color: { rgb: "1F497D" } },
        alignment: { horizontal: "center", vertical: "center" },
      },
    };
  
    // Merge company name across all columns
    ws["!merges"] = [
      { s: { r: rowCursor, c: 0 }, e: { r: rowCursor, c: columns.length - 1 } },
    ];
    rowCursor += 1;
  
    // --- GENERATED ON (Right-Aligned Below Company Name) ---
    ws[XLSX.utils.encode_cell({ r: rowCursor, c: columns.length - 1 })] = {
      v: `Generated on: ${generatedOn}`,
      t: "s",
      s: {
        font: { italic: true, color: { rgb: "808080" } },
        alignment: { horizontal: "right", vertical: "center" },
      },
    };
    rowCursor += 2;
  
    // --- REPORT TITLE ---
    ws[XLSX.utils.encode_cell({ r: rowCursor, c: 0 })] = {
      v: title || "Report",
      t: "s",
      s: {
        font: { bold: true, sz: 14, color: { rgb: "000000" } },
        alignment: { horizontal: "center", vertical: "center" },
      },
    };
  
    // Merge title row as well
    ws["!merges"].push({
      s: { r: rowCursor, c: 0 },
      e: { r: rowCursor, c: columns.length - 1 },
    });
    rowCursor += 2;
  
    // --- FILTERS SECTION ---
    if (headerInfo?.filters && Object.keys(headerInfo.filters).length > 0) {
      Object.entries(headerInfo.filters).forEach(([key, value]) => {
        ws[XLSX.utils.encode_cell({ r: rowCursor, c: 0 })] = {
          v: `${key}: ${value}`,
          t: "s",
          s: {
            font: { color: { rgb: "404040" } },
            alignment: { horizontal: "left", vertical: "center" },
          },
        };
        rowCursor++;
      });
      rowCursor++;
    }
  
    // --- TABLE HEADER ---
    columns.forEach((col, colIdx) => {
      const cellRef = XLSX.utils.encode_cell({ r: rowCursor, c: colIdx });
      ws[cellRef] = {
        v: extractTextFromJSX(col.name),
        t: "s",
        s: {
          font: { bold: true },
          fill: { fgColor: { rgb: "E0E0E0" } },
          alignment: { horizontal: "center", vertical: "center" },
          border: {
            top: { style: "thin", color: { rgb: "A0A0A0" } },
            bottom: { style: "thin", color: { rgb: "A0A0A0" } },
            left: { style: "thin", color: { rgb: "A0A0A0" } },
            right: { style: "thin", color: { rgb: "A0A0A0" } },
          },
        },
      };
    });
  
    // --- TABLE DATA ---
    data.forEach((row, rowIdx) => {
      columns.forEach((col, colIdx) => {
        const cellRef = XLSX.utils.encode_cell({ r: rowCursor + 1 + rowIdx, c: colIdx });
        let value =
          typeof col.selector === "function" ? col.selector(row) : row[col.selector];
        if (React.isValidElement(value)) value = extractTextFromJSX(value);
        if (typeof value === "object" && value !== null) value = JSON.stringify(value);
        ws[cellRef] = {
          v: value ?? "",
          t: "s",
          s: {
            alignment: { horizontal: "left", vertical: "center" },
            border: {
              top: { style: "thin", color: { rgb: "DDDDDD" } },
              bottom: { style: "thin", color: { rgb: "DDDDDD" } },
              left: { style: "thin", color: { rgb: "DDDDDD" } },
              right: { style: "thin", color: { rgb: "DDDDDD" } },
            },
          },
        };
      });
    });
  
    // --- SHEET RANGE & COLUMN WIDTHS ---
    ws["!ref"] = XLSX.utils.encode_range({
      s: { r: 0, c: 0 },
      e: { r: rowCursor + data.length + 1, c: columns.length - 1 },
    });
  
    ws["!cols"] = columns.map((col) => ({
      wch: Math.max(15, extractTextFromJSX(col.name).length + 5),
    }));
  
    XLSX.utils.book_append_sheet(wb, ws, title || "Sheet1");
  
    const fileName = getFileName(title);
    XLSX.writeFile(wb, `${fileName}.xlsx`);
  };
  
  

// ---------------- PDF Export ----------------
export const exportToPDF = async (title, columns, data, headerInfo = {}) => {
    // ✅ Decide orientation dynamically
    const isWide =
      columns.length > 6 ||
      columns.some((col) => extractTextFromJSX(col.name).length > 20);
    const orientation = isWide ? "landscape" : "portrait";
    const doc = new jsPDF({ orientation });
    const pageWidth = doc.internal.pageSize.getWidth();
  
    // --- Fetch company info ---
    const companyInfo = await getCompanyInfo();
    const companyName = companyInfo?.CompanyName || "Company";
    const logoPath = companyInfo?.LogoImagePath
      ? `/assets/img/${companyInfo.LogoImagePath}`
      : null;
  
    const now = new Date();
    const generatedOn = now.toLocaleString();
  
    let yPos = 20;
  
    // --- LOGO & COMPANY INFO ---
    let logoHeight = 0;
    if (logoPath) {
      try {
        const img = new Image();
        img.crossOrigin = "Anonymous";
        img.src = logoPath;
  
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
        });
  
        const logoWidth = 60;
        logoHeight = 22;
        doc.addImage(img, "PNG", 10, 10, logoWidth, logoHeight); // (x, y, width, height)
      } catch (e) {
        console.warn("Could not load company logo:", e);
      }
    }
  
    // --- COMPANY NAME ---
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text(companyName, 80, 20); // aligned next to logo
  
    // --- GENERATED DATE (top right) ---
    doc.setFont("helvetica", "italic");
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generated on: ${generatedOn}`, pageWidth - 14, 15, { align: "right" });
  
    // --- REPORT TITLE ---
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(0);
    doc.text(title || "Report", 14, logoHeight + 20); // below logo/company
    yPos = logoHeight + 28;
  
    // --- FILTERS ---
    if (headerInfo?.filters && Object.keys(headerInfo.filters).length > 0) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(50);
      Object.entries(headerInfo.filters).forEach(([key, value]) => {
        doc.text(`${key}: ${value}`, 14, yPos);
        yPos += 6;
      });
      yPos += 4;
    }
  
    // --- TABLE CONTENT ---
    const tableColumn = columns.map((col) => extractTextFromJSX(col.name));
    const tableRows = data.map((row) =>
      columns.map((col) => {
        let value =
          typeof col.selector === "function" ? col.selector(row) : row[col.selector];
        if (React.isValidElement(value)) value = extractTextFromJSX(value);
        if (typeof value === "object" && value !== null) value = JSON.stringify(value);
        return value ?? "";
      })
    );
  
    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: yPos,
      styles: { fontSize: 9, cellPadding: 2 },
      headStyles: {
        fillColor: [224, 224, 224],
        textColor: 0,
        fontStyle: "bold",
      },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      margin: { top: 15 },
    });
  
    const fileName = getFileName(title);
    doc.save(`${fileName}.pdf`);
  };
  
